import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { FinecoDownloadButton } from "pages/assistant/components/FinecoDownloadButton";
import { AssistantService } from "shared/services/AssistantService";
import { useToast } from "shared/components/ui/use-toast";
import useThreadsStore from "shared/store/threadsStore";
import { GenerateComparisonReport } from "./GenerateComparisonReport";
import { getComparisonPdfFilename } from "utils/comparisonUtils";

import styles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";

type FinecoPortfolioCompareButtonsProps = {
  threadId: string;
  portfolioId1: string;
  portfolioId2: string;
};

export const FinecoPortfolioCompareButtons: React.FC<FinecoPortfolioCompareButtonsProps> = ({
  threadId,
  portfolioId1,
  portfolioId2,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const stories = useThreadsStore((state) => state.stories);
  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
  const [generatingDownloadPdf, setGeneratingDownloadPdf] = useState<boolean>(false);
  const generateReportRef = useRef(null);

  const requestGenerateComparisonPdf = useCallback(
    async (reportSpec: any, fromReport: boolean = false) => {
      if (fromReport) {
        setGeneratingPdf(true);
      }

      try {
        let rawPdf;
        
        if (portfolioId1 && portfolioId2) {
          rawPdf = await AssistantService.createComparisonPdf(
            portfolioId1,
            portfolioId2,
            reportSpec,
          );
        } else {
          throw new Error("No portfolio IDs available for comparison PDF generation");
        }

        const blob = new Blob([rawPdf], { type: "application/pdf" });
        if (blob.type === "application/pdf") {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = getComparisonPdfFilename(portfolioId1, portfolioId2);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          toast({
            description: t("portafoglioCompare.downloadedSuccess"),
            title: t("global.success"),
          });
        } else {
          toast({
            description: t("portafoglioCompare.downloadError"),
            title: t("global.error"),
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error generating portfolio comparison PDF", error);
        let errorMessage = t("portafoglioCompare.downloadError");

        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        toast({
          description: errorMessage,
          title: t("global.error"),
          variant: "destructive",
        });
      } finally {
        setGeneratingPdf(false);
        setGeneratingDownloadPdf(false);
      }
    },
    [threadId, portfolioId1, portfolioId2, t, toast],
  );

  return (
    <div className={styles.buttonContainer}>
      <FinecoDownloadButton
        click={() => {
          setGeneratingDownloadPdf(true);
          generateReportRef.current.generate(false);
        }}
        copy={
          !generatingDownloadPdf
            ? t("generateReport.download")
            : t("generateReport.downloading")
        }
        disabled={generatingDownloadPdf}
      />
      <GenerateComparisonReport
        threadId={threadId}
        portfolioId1={portfolioId1}
        portfolioId2={portfolioId2}
        onGenerate={requestGenerateComparisonPdf}
        ref={generateReportRef}
      >
        <div className={styles.generateReportButton}>
          {!generatingPdf
            ? t("generateReport.modify").toUpperCase()
            : t("generateReport.downloading").toUpperCase()}
        </div>
      </GenerateComparisonReport>
    </div>
  );
};